import { useState, useCallback } from 'react';
import { useToast } from '@/app/providers/ToastProvider';
import { toMonthly } from '@/shared/utils/formatCurrency';
import { billService } from '../services/billService';

const emptyCustomSchedule = {
  type: 'every-n-weeks',
  intervalWeeks: 2,
  dayOfWeek: 4, // Thursday
  nth: 2,
  anchorDate: '',
};

const emptyForm = {
  name: '',
  amount: '',
  category: '',
  frequency: 'Monthly',
  dueDate: '',
  autoPay: false,
  autoGenerate: true,
  customSchedule: emptyCustomSchedule,
};

export function useBills({ householdId, data, bills }) {
  const toast = useToast();
  const [billForm, setBillForm] = useState(emptyForm);
  const [billErrors, setBillErrors] = useState({});
  const [editBillId, setEditBillId] = useState(null);

  const totalBillsMonthly = bills.reduce((s, b) => s + toMonthly(b.amount, b.frequency, b.customSchedule), 0);

  const resetForm = () => {
    setBillForm(emptyForm);
    setBillErrors({});
    setEditBillId(null);
  };

  const openAddBill = useCallback(() => {
    resetForm();
  }, []);

  const openEditBill = useCallback((bill) => {
    setEditBillId(bill.id);
    setBillErrors({});
    const storedDueDate = bill.dueDate || '';
    const isISO = /^\d{4}-\d{2}-\d{2}$/.test(storedDueDate);
    setBillForm({
      name: bill.name || '',
      amount: bill.amount != null ? String(bill.amount) : '',
      category: bill.category || '',
      frequency: bill.frequency || 'Monthly',
      // Only populate picker when it's already an ISO string; legacy "15th" renders empty.
      dueDate: isISO ? storedDueDate : '',
      // Carry legacy string so it round-trips when user saves without picking a new date.
      _legacyDueDate: !isISO && storedDueDate ? storedDueDate : undefined,
      autoPay: !!bill.autoPay,
      autoGenerate: bill.autoGenerate !== false,
      customSchedule: bill.customSchedule || emptyCustomSchedule,
    });
  }, []);

  const saveBill = useCallback(async () => {
    let success;
    if (editBillId) {
      success = await billService.update(householdId, data, editBillId, billForm);
      if (success) toast.success('Bill updated');
    } else {
      success = await billService.add(householdId, data, billForm);
      if (success) toast.success('Bill added');
    }
    if (success) resetForm();
    return success;
  }, [householdId, data, billForm, editBillId, toast]);

  const removeBill = useCallback((billId, cascade = false) => {
    billService.remove(householdId, data, billId, cascade);
    toast.success(cascade ? 'Bill and its transactions removed' : 'Bill removed');
  }, [householdId, data, toast]);

  return {
    billForm, setBillForm,
    billErrors, setBillErrors,
    editBillId,
    openAddBill, openEditBill,
    totalBillsMonthly,
    saveBill, removeBill,
  };
}
